export const ROUTES = {
    HOME: "/",
    /** Where everything stands: what waits on the person, the tiles, the past two weeks, the pipeline. */
    OVERVIEW: "/overview",
    /** Your envoy: off with its two switches until turned on, then what it does and looks for. */
    ENVOY: "/envoy",
    OPENINGS: "/openings",
    HISTORY: "/history",
    RESUME_EDIT: "/resume/edit",
    RESUME_WORK: "/resume/work",
    PRICING: "/pricing",
    JOBS: "/jobs",
    QUEUE: "/queue",
    APPLICATIONS: "/applications",
    RESUME: "/resume",
    /** The postings a person kept for later. */
    SAVED: "/jobs/saved",
    /** Where Stripe Checkout comes back to once a plan is bought: a thank you, then the jobs. */
    SUBSCRIBED: "/subscribed",
    /** The support form, for anyone. */
    SUPPORT: "/support",
    /** The person's account: which emails they get, and their time zone. */
    ACCOUNT: "/account",
    /** The account's Plan and billing box: the plan, Upgrade, and Stripe's portal. */
    BILLING: "/account#billing",
    /**
     * What Job Envoy may email a person, signed out: the page the footer of
     * every email links to, with a signed `?t=` naming whose it is. Served
     * outside the sign-in gate.
     */
    EMAIL_CHOICES: "/email/choices",
    /** The privacy policy and the terms, signed out, for anyone: the stores link here. */
    PRIVACY: "/privacy",
    TERMS: "/terms",
    /** The free tools, signed out, for anyone: the one place that lists them. */
    TOOLS: "/tools",
    /** The free resume keyword checker. No ad tag runs on it (`isToolPath`). */
    RESUME_CHECK: "/tools/resume-keyword-checker",
    /** The checker, opened on one posting's text. Reached through `resumeCheckPath`. */
    RESUME_CHECK_POSTING: "/tools/resume-keyword-checker/:postingId",

    // Detail routes. Parameterised, so they are reached through the helpers
    // below rather than referenced directly outside the router.
    POSTING_DETAIL: "/jobs/posting/:postingId",
    /** Every job of one company. Reached through `companyPath`. */
    COMPANY: "/jobs/company/:source/:company",
} as const;

/**
 * The Jobs page's addresses: the site's home, the old `/jobs`, For you
 * (`/openings`), which is the same board on the envoy's filters, and one
 * company's jobs, where the search box searches them.
 */
export function isJobsPath(pathname: string): boolean {
    return (
        pathname === ROUTES.HOME ||
        pathname === ROUTES.JOBS ||
        pathname === ROUTES.OPENINGS ||
        pathname.startsWith("/jobs/company/")
    );
}

/**
 * The address as the router reads it: each escaped ASCII character
 * decoded, one by one so a malformed escape elsewhere cannot throw or hide
 * it. The router matches `/%74ools` and `/Tools` to the tools, and the
 * browser keeps the form it was typed in.
 */
function asRouted(pathname: string): string {
    return pathname.replace(/%[0-7][0-9a-f]/gi, (escaped) =>
        String.fromCharCode(Number.parseInt(escaped.slice(1), 16)),
    );
}

/**
 * The tool pages' addresses: `/tools` and anything under `/tools/`, in any
 * case and however escaped, as the router reads them, never a name that
 * only starts with it. A visitor pastes a resume on these, so no ad tag
 * may run on them (lib/ads).
 */
export function isToolPath(pathname: string): boolean {
    const routed = asRouted(pathname).toLowerCase();
    return routed === ROUTES.TOOLS || routed.startsWith(`${ROUTES.TOOLS}/`);
}

/** The checker, opened on one posting's text. */
export function resumeCheckPath(postingId: string): string {
    return `${ROUTES.RESUME_CHECK}/${encodeURIComponent(postingId)}`;
}

/**
 * Links to one posting: the record of everything that happened to it. A
 * route rather than an overlay, so "what did I do about this one" is
 * something that can be bookmarked. The jobs page also opens a posting
 * in a drawer beside the list, with `?posting=` on its own URL.
 */
export function postingDetailPath(postingId: string): string {
    return `/jobs/posting/${encodeURIComponent(postingId)}`;
}

/** The jobs page, the site's home, with these words, filters or posting open. */
export function jobsPathWith(params: URLSearchParams): string {
    const query = params.toString();
    return query ? `${ROUTES.HOME}?${query}` : ROUTES.HOME;
}
